import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import './IntegrationHub.css';

// App logos from /public/companyLOGO
const apps = [
  // China Ecosystem
  { id: 'wechat', name: 'WeChat', logo: '/companyLOGO/WeChat_logo.svg.png', category: 'china' },
  { id: 'wecom', name: 'WeCom', logo: '/companyLOGO/wecome.png', category: 'china' },
  { id: 'dingtalk', name: 'DingTalk', logo: '/companyLOGO/dingding.webp', category: 'china' },
  { id: 'feishu', name: 'Feishu', logo: '/companyLOGO/Feishu.webp', category: 'china' },
  { id: 'xiaohongshu', name: 'Xiaohongshu', logo: '/companyLOGO/XiaohongshuLOGO.png', category: 'china' },
  
  // Global Ecosystem
  { id: 'salesforce', name: 'Salesforce', logo: '/companyLOGO/Salesforce.com_logo.svg.png', category: 'global' },
  { id: 'slack', name: 'Slack', logo: '/companyLOGO/Slack_Technologies_Logo.svg.png', category: 'global' },
  { id: 'teams', name: 'Microsoft Teams', logo: '/companyLOGO/Microsoft-Teams-Logo.png', category: 'global' },
  { id: 'hubspot', name: 'HubSpot', logo: '/companyLOGO/HubSpot-Logo.png', category: 'global' },
  { id: 'chatgpt', name: 'ChatGPT', logo: '/companyLOGO/ChatGPT-Logo.svg.png', category: 'global' },
  { id: 'whatsapp', name: 'WhatsApp', logo: '/companyLOGO/whatsapp.png', category: 'global' },
  { id: 'more', name: 'More...', logo: null, category: 'other' }
];

const IntegrationHub = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const { language } = useLanguage();
  const t = getTranslation(language);

  // State for responsive app count
  const [visibleAppsCount, setVisibleAppsCount] = useState(apps.length);

  useEffect(() => {
    const updateVisibleApps = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setVisibleAppsCount(6);
      } else if (width <= 640) {
        setVisibleAppsCount(8);
      } else if (width <= 1024) {
        setVisibleAppsCount(10);
      } else {
        setVisibleAppsCount(apps.length);
      }
    };

    updateVisibleApps();
    window.addEventListener('resize', updateVisibleApps);
    return () => window.removeEventListener('resize', updateVisibleApps);
  }, []);

  const visibleApps = apps.slice(0, visibleAppsCount);

  return (
    <section className="integration-hub" ref={ref}>
      <div className="integration-hub-container">
        {/* Header */}
        <div className="integration-hub-header">
          <h2 className="integration-hub-title">{t.integration.title}</h2>
          <p className="integration-hub-subtitle">
            {t.integration.subtitle}
          </p>
        </div>

        {/* Orbit Diagram */}
        <div className="orbit-container">
          {/* Background gradient rings */}
          <div className="hub-gradient-ring ring-1"></div>
          <div className="hub-gradient-ring ring-2"></div>
          <div className="hub-gradient-ring ring-3"></div>

          {/* Central Hub */}
          <div className="central-hub">
            <img src="/logo_vertical.png" alt="Agentecfusion" className="hub-logo" />
          </div>

          {/* Orbit Circle and Apps */}
          <div className="orbit-ring" key={`orbit-${visibleAppsCount}`}>
            {/* Orbit circle SVG */}
            <svg className="orbit-circle" viewBox="0 0 640 640">
              <circle
                cx="320"
                cy="320"
                r="280"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeDasharray="4 4"
              />
            </svg>

            {/* App Icons on Orbit */}
            {visibleApps.map((app, index) => {
              // Use percentage-based positioning for responsive design
              // Start from top (-90deg) and distribute evenly
              const angle = (index / visibleApps.length) * 2 * Math.PI - (Math.PI / 2);
              // orbit-container is 750px, orbit-circle SVG is 640px with r=280
              // So radius relative to container = 280 / 375 (half of 750) = 74.67%
              // But we position from center (50%), so we use 280/750 * 100 = 37.33%
              const radiusPercent = 37.33;
              const xPercent = Math.cos(angle) * radiusPercent;
              const yPercent = Math.sin(angle) * radiusPercent;
              return (
                <div
                  key={app.id}
                  className="app-icon"
                  style={{
                    left: `calc(50% + ${xPercent}%)`,
                    top: `calc(50% + ${yPercent}%)`,
                    '--initial-rotation': '0deg'
                  }}
                >
                  <div className="app-placeholder">
                    {app.logo ? (
                      <img 
                        src={app.logo} 
                        alt={app.name} 
                        className="app-logo"
                        title={app.name}
                        onError={(e) => {
                          console.error(`Failed to load image: ${app.logo}`);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <span className="app-name">{app.name}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Caption */}
        <div className="integration-hub-footer">
          <p className="integration-hub-caption">
            {t.integration.customIntegration.split('Contact us').map((part, i) => 
              i === 0 ? part : <><a href="#contact">Contact us</a>{part}</>
            )}
          </p>
        </div>
      </div>
    </section>
  );
};

export default IntegrationHub;
